import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, TextField, FormControl, FormGroup, FormControlLabel, Checkbox, Button, Grid, Card, CardContent, Typography, Autocomplete, InputLabel, Select, MenuItem, Paper, Divider, Accordion, AccordionSummary, AccordionDetails, Chip, Alert, Stack, Avatar, CircularProgress, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import SecurityIcon from '@mui/icons-material/Security';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import LockIcon from '@mui/icons-material/Lock';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { createUser } from 'User/Domain/Services/CreateUserService';
import { useParams, useNavigate } from 'react-router-dom';
import { patchUser } from 'User/Domain/Services/PatchUserService';
import { getUser } from 'User/Domain/Services/GetUserService';
import { getUserPhotoSuggestions, selectUserPhoto } from 'User/Domain/Services/UserPhotoSuggestionsService';
import { getAuthenticatedAvatarUrl, useAvatarBlobSubscription, invalidateAvatarCache } from 'Shared/Utils/Domain/Services/AvatarUrlService';
import { getPermissionsList } from 'Permission/Domain/Services/GetPermissionsListService';
import { toast } from "react-toastify";
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { getPermissionGroupsList } from 'PermissionGroup/Domain/Services/GetPermissionGroupsListService';
import { getCompanyList } from 'Company/Domain/Services/GetCompanyListService';
import { getDelegationList } from 'Delegation/Domain/Services/GetDelegationListService';
import { getJobPositionsList } from 'JobPosition/Domain/Services/GetJobPositionsListService';
import { getProfessionalCategoriesList } from 'ProfessionalCategory/Domain/Services/GetProfessionalCategoriesListService';
import { PRIVATE_ADMIN_LIST_USERS, PRIVATE_USER_LIST_USERS } from '../Constants/PathsConstant';
import { useTranslation } from 'react-i18next';
import { MANAGE_USER_PHOTOS, WRITE_USERS } from 'Shared/Security/Application/Constants/PermissionsConstant';

function FormUserView() {
  useAvatarBlobSubscription();
  const { t } = useTranslation();
  const { isAdmin, checkPermission, refreshMe, userState, impersonate, getMeFromLocalStorage } = useAuthContext();
  const canManagePhotos = isAdmin() || checkPermission(MANAGE_USER_PHOTOS) || checkPermission(WRITE_USERS);
  const canEditUserData = isAdmin() || checkPermission(WRITE_USERS);
  const { id } = useParams();
  const navigate = useNavigate();
  const [openImpersonateModal, setOpenImpersonateModal] = useState(false);

  // Security variables
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');
  const [role, setRole] = useState('ROLE_USER');
  const [enabled, setEnabled] = useState(true);

  // Lists
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [inheritedPermissions, setInheritedPermissions] = useState([]);
  const [getPermissions, setGetPermissions] = useState(canEditUserData);

  const [permissionGroups, setPermissionGroups] = useState([]);
  const [selectedPermissionGroups, setSelectedPermissionGroups] = useState([]);
  const [getPermissionGroups, setGetPermissionGroups] = useState(canEditUserData);

  const [companies, setCompanies] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const [professionalCategories, setProfessionalCategories] = useState([]);
  const [getSelectData, setGetSelectData] = useState(canEditUserData);

  const [sendingApiRequest, setSendingApiRequest] = useState(false);

  // New variables
  const [name, setName] = useState('');
  const [firstSurname, setFirstSurname] = useState('');
  const [secondSurname, setSecondSurname] = useState('');
  const [dni, setDni] = useState('');
  const [gender, setGender] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [workPhone, setWorkPhone] = useState('');
  const [extension, setExtension] = useState('');
  const [shortName, setShortName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [delegationId, setDelegationId] = useState('');
  const [jobPositionId, setJobPositionId] = useState('');
  const [professionalCategoryId, setProfessionalCategoryId] = useState('');
  const [workerId, setWorkerId] = useState('');
  const [iban, setIban] = useState('');
  const [isDirectEmployee, setIsDirectEmployee] = useState(false);
  const [hireDate, setHireDate] = useState('');
  const [terminationDate, setTerminationDate] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [commissionsCommercialExtras, setCommissionsCommercialExtras] = useState(false);
  const [commissionsAnnexes, setCommissionsAnnexes] = useState(false);

  // Photo suggestions state
  const [photoSuggestions, setPhotoSuggestions] = useState([]);
  const [selectedPhotoFilename, setSelectedPhotoFilename] = useState(null);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(null);
  const [photoSource, setPhotoSource] = useState(null);
  const [photoSearchQuery, setPhotoSearchQuery] = useState('');
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);

  const userInitials = useMemo(() => {
    const parts = [name, firstSurname].filter(Boolean).map((s) => (s.trim ? s.trim()[0] : s[0])?.toUpperCase());
    return parts.join('') || (name ? name.charAt(0).toUpperCase() : 'U');
  }, [name, firstSurname]);

  const loadPhotoSuggestions = useCallback(async (query = '', forceRefresh = false) => {
    if (!id || id === 'new') return;
    setLoadingPhotos(true);
    try {
      const res = await getUserPhotoSuggestions(id, query, forceRefresh);
      if (res) {
        setPhotoSuggestions(res.suggestions || []);
        setSelectedPhotoFilename(res.selectedFilename || null);
        setCurrentAvatarUrl(res.currentAvatar || `/api/v1/user/${id}/avatar`);
        setPhotoSource(res.photoSource || null);
      }
    } catch {
      // Ignore error
    } finally {
      setLoadingPhotos(false);
    }
  }, [id]);

  const handleSelectPhoto = async (filename) => {
    if (!id || id === 'new' || savingPhoto) return;
    setSavingPhoto(true);
    try {
      const res = await selectUserPhoto(id, filename);
      if (res && res.success) {
        setSelectedPhotoFilename(res.selectedFilename || null);
        setCurrentAvatarUrl(res.currentAvatar || `/api/v1/user/${id}/avatar`);
        if (Array.isArray(res.suggestions)) {
          setPhotoSuggestions(res.suggestions);
        }
        if (res.photoSource) {
          setPhotoSource(res.photoSource);
        }
        invalidateAvatarCache(`/user/${id}/avatar`);
        if (userState && String(userState.id) === String(id) && typeof refreshMe === 'function') {
          await refreshMe();
        }
        toast.success(res.message || 'Foto actualizada correctamente.');
      }
    } catch {
      toast.error('No se pudo asignar la foto seleccionada.');
    } finally {
      setSavingPhoto(false);
    }
  };

  const handleConfirmImpersonate = async () => {
    if (!id || id === 'new') return;
    setSendingApiRequest(true);
    try {
      await impersonate(id);
    } catch (error) {
      toast.error('Error al entrar como usuario');
      setSendingApiRequest(false);
      setOpenImpersonateModal(false);
    }
  };

  useEffect(() => {
    if (id !== 'new') {
      loadPhotoSuggestions();
    }
  }, [id, loadPhotoSuggestions]);

  useEffect(() => {
    if (!canEditUserData && !canManagePhotos) {
      navigate(PRIVATE_USER_LIST_USERS);
    } else if (!canEditUserData && id === 'new') {
      navigate(PRIVATE_USER_LIST_USERS);
    }
  }, [canEditUserData, canManagePhotos, id, navigate]);

  useEffect(() => {
    if (id !== "new") {
      setSendingApiRequest(true);
      getUser(id)
        .then((response) => {
          if (!response) {
            return;
          }
          setEmail(response.email || '');
          setRole((response.roles && response.roles.length > 0) ? response.roles[0] : 'ROLE_USER');
          setEnabled(typeof response.enabled === 'boolean' ? response.enabled : true);
          
          setName(response.name || '');
          setFirstSurname(response.firstSurname || '');
          setSecondSurname(response.secondSurname || '');
          setDni(response.dni || '');
          setGender(response.gender || '');
          setPersonalPhone(response.personalPhone || '');
          setWorkPhone(response.workPhone || '');
          setExtension(response.extension || '');
          setShortName(response.shortName || '');
          setCompanyId(response.company ? response.company.id : '');
          setDelegationId(response.delegation ? response.delegation.id : '');
          setJobPositionId(response.jobPosition ? response.jobPosition.id : '');
          setProfessionalCategoryId(response.professionalCategory ? response.professionalCategory.id : '');
          setWorkerId(response.workerId || '');
          setIban(response.iban || '');
          setIsDirectEmployee(response.isDirectEmployee || false);
          setHireDate(response.hireDate ? response.hireDate.split('T')[0] : '');
          setTerminationDate(response.terminationDate ? response.terminationDate.split('T')[0] : '');
          setBirthDate(response.birthDate ? response.birthDate.split('T')[0] : '');
          setCommissionsCommercialExtras(response.commissionsCommercialExtras || false);
          setCommissionsAnnexes(response.commissionsAnnexes || false);

          if (Array.isArray(response.permissions) && response.permissions.length > 0) {
            var perms = [];
            response.permissions.forEach((permission) => {
              if (permission && permission.sku) perms.push(permission.sku);
            });
            setSelectedPermissions(perms);
          }
          if (Array.isArray(response.permissionGroups) && response.permissionGroups.length > 0) {
            var permGroups = [];
            response.permissionGroups.forEach((permissionGroup) => {
              if (permissionGroup && permissionGroup.sku) permGroups.push(permissionGroup.sku);
            });
            setSelectedPermissionGroups(permGroups);
          }
          if (Array.isArray(response.inheritedPermissions)) {
            setInheritedPermissions(response.inheritedPermissions);
          }
        })
        .catch((error) => {
          console.error("Error loading user:", error);
          toast.error("Error al cargar los datos del usuario.");
        })
        .finally(() => {
          setSendingApiRequest(false);
        });
    }
  }, [id]);

  useEffect(() => {
    if (!canEditUserData) {
      setGetPermissions(false);
      setGetPermissionGroups(false);
      setGetSelectData(false);
      return;
    }
    if (getPermissions) {
      getPermissionsList({ limit: 1000 })
        .then((response) => {
          setPermissions(response?.records || []);
          setGetPermissions(false);
        })
        .catch(() => {
          setGetPermissions(false);
        });
    }
    if (getPermissionGroups) {
      getPermissionGroupsList()
        .then((response) => {
          setPermissionGroups(response?.records || []);
          setGetPermissionGroups(false);
        })
        .catch(() => {
          setGetPermissionGroups(false);
        });
    }
    if (getSelectData) {
      Promise.all([
        getCompanyList(),
        getDelegationList({limit: 9999}),
        getJobPositionsList({limit: 9999}),
        getProfessionalCategoriesList({limit: 9999})
      ])
        .then(([compRes, delRes, jobRes, profRes]) => {
          if (compRes) setCompanies(compRes?.records || []);
          if (delRes) setDelegations(delRes?.records || []);
          if (jobRes) setJobPositions(jobRes?.records || []);
          if (profRes) setProfessionalCategories(profRes?.records || []);
          setGetSelectData(false);
        })
        .catch(() => {
          setGetSelectData(false);
        });
    }
  }, [canEditUserData, getPermissions, getPermissionGroups, getSelectData]);

  const handlePermissionChange = (event) => {
    const value = event.target.value;
    const currentIndex = selectedPermissions.indexOf(value);
    const newSelectedPermissions = [...selectedPermissions];
    if (currentIndex === -1) {
      newSelectedPermissions.push(value);
    } else {
      newSelectedPermissions.splice(currentIndex, 1);
    }
    setSelectedPermissions(newSelectedPermissions);
  };
  
  const handlePermissionGroupChange = (event) => {
    const value = event.target.value;
    const currentIndex = selectedPermissionGroups.indexOf(value);
    const newSelectedPermissionGroups = [...selectedPermissionGroups];
    if (currentIndex === -1) {
      newSelectedPermissionGroups.push(value);
    } else {
      newSelectedPermissionGroups.splice(currentIndex, 1);
    }
    setSelectedPermissionGroups(newSelectedPermissionGroups);
  };

  const currentJobPosition = useMemo(() => {
    return jobPositions.find((jp) => String(jp.id) === String(jobPositionId)) || null;
  }, [jobPositions, jobPositionId]);

  const inheritedSkus = useMemo(() => {
    return (inheritedPermissions || []).map((p) => p.sku).filter(Boolean);
  }, [inheritedPermissions]);

  const permissionsByCategory = useMemo(() => {
    const grouped = {};
    permissions.forEach((perm) => {
      const cat = perm.category || 'GENERAL';
      if (!grouped[cat]) {
        grouped[cat] = [];
      }
      grouped[cat].push(perm);
    });
    return grouped;
  }, [permissions]);

  const handleToggleCategory = (catPermissions) => {
    const catSkus = catPermissions.map((p) => p.sku).filter((sku) => !inheritedSkus.includes(sku));
    const allSelected = catSkus.length > 0 && catSkus.every((s) => selectedPermissions.includes(s));
    if (allSelected) {
      setSelectedPermissions(selectedPermissions.filter((s) => !catSkus.includes(s)));
    } else {
      const toAdd = catSkus.filter((s) => !selectedPermissions.includes(s));
      setSelectedPermissions([...selectedPermissions, ...toAdd]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      var apiResponse = null;
      if (password !== passwordTwo) {
        toast.error("Las contraseñas no coinciden");
        return;
      }
      if (sendingApiRequest) return;
      setSendingApiRequest(true);

      const payload = {
        email,
        roles: [role],
        enabled,
        permissions: selectedPermissions,
        permissionGroups: selectedPermissionGroups,
        name,
        firstSurname,
        secondSurname,
        dni,
        gender,
        personalPhone,
        workPhone,
        extension,
        shortName,
        companyId: companyId || null,
        delegationId: delegationId || null,
        jobPositionId: jobPositionId || null,
        professionalCategoryId: professionalCategoryId || null,
        workerId,
        iban,
        isDirectEmployee,
        hireDate: hireDate || null,
        terminationDate: terminationDate || null,
        birthDate: birthDate || null,
        commissionsCommercialExtras,
        commissionsAnnexes
      };

      if (id === "new") {
        apiResponse = await createUser({
          ...payload,
          password: password,
        });
      } else {
        apiResponse = await patchUser(id, {
          ...payload,
          password: password ? password : null,
        });
      }
      
      setSendingApiRequest(false);
      if (apiResponse !== null) {
        if (isAdmin()) {
          navigate(PRIVATE_ADMIN_LIST_USERS);
        } else {
          navigate(PRIVATE_USER_LIST_USERS);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 2, md: 4 }, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, maxWidth: 1200, mx: 'auto', backgroundColor: '#ffffff' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', md: 'flex-start' }, mb: 3, gap: 2 }}>
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#1a237e' }}>
              {!canEditUserData && canManagePhotos
                ? 'Gestión de Foto de Perfil'
                : (id === "new" ? t('user.form.create') : t('user.form.update'))}
            </Typography>
            <Typography variant="body1" sx={{ color: '#607d8b' }}>
              {!canEditUserData && canManagePhotos
                ? `Selecciona o modifica la foto corporativa de Azure Blob Storage para ${[name, firstSurname, secondSurname].filter(Boolean).join(' ') || 'este usuario'}.`
                : (id === "new" ? 'Rellena los campos a continuación para crear un nuevo usuario en el sistema.' : 'Modifica la información del usuario en las siguientes secciones.')}
            </Typography>
          </Box>
          {isAdmin() && id !== "new" && String(id) !== String(getMeFromLocalStorage()?.id) && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<SwitchAccountIcon />}
              onClick={() => setOpenImpersonateModal(true)}
              sx={{
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 'bold',
                px: 2.5,
                py: 1.2,
                boxShadow: '0 4px 12px rgba(237, 108, 2, 0.25)',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              Entrar como usuario
            </Button>
          )}
        </Box>

        {!canEditUserData && canManagePhotos && (
          <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
            Dispones de permisos para <strong>gestionar la foto de perfil y sugerencias de Azure Blob</strong> de este usuario. Los cambios de foto se guardan automáticamente al seleccionarlas.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>

            {/* Foto de Perfil y Sugerencias de Blob Storage */}
            {id !== "new" && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2, border: 'none', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhotoCameraIcon sx={{ color: '#257eb9', mr: 1, fontSize: 28 }} />
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="#333">
                            Foto de Perfil y Sugerencias (Blob Storage)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Selecciona cuál de las fotos encontradas en Azure Blob Storage (`fotos_rrhh`) se mostrará para este usuario.
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          placeholder="Buscar otra foto en el Blob (nombre o DNI)..."
                          value={photoSearchQuery}
                          onChange={(e) => setPhotoSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              loadPhotoSuggestions(photoSearchQuery, false);
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#257eb9', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ minWidth: { xs: '100%', sm: 280 } }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => loadPhotoSuggestions(photoSearchQuery, false)}
                          disabled={loadingPhotos}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Buscar
                        </Button>
                        <IconButton
                          size="small"
                          title="Recargar índice de Blob Storage"
                          onClick={() => loadPhotoSuggestions(photoSearchQuery, true)}
                          disabled={loadingPhotos}
                          sx={{ color: '#257eb9', border: '1px solid #cbd5e1' }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                        {selectedPhotoFilename && (
                          <Button
                            variant="text"
                            color="warning"
                            size="small"
                            disabled={savingPhoto}
                            onClick={() => handleSelectPhoto(null)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            Restaurar automático
                          </Button>
                        )}
                        {selectedPhotoFilename !== 'none' && (
                          <Button
                            variant="text"
                            color="inherit"
                            size="small"
                            disabled={savingPhoto}
                            onClick={() => handleSelectPhoto('none')}
                            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
                          >
                            Sin foto
                          </Button>
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3} alignItems="flex-start">
                      {/* Current Active Photo */}
                      <Grid item xs={12} md={3} sx={{ alignSelf: 'flex-start' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            p: 2.5,
                            borderRadius: 3,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', mb: 1.5, letterSpacing: 0.5 }}>
                            Foto mostrada actualmente
                          </Typography>
                          <Avatar
                            src={selectedPhotoFilename === 'none' ? undefined : (getAuthenticatedAvatarUrl(currentAvatarUrl) || undefined)}
                            alt={name}
                            sx={{
                              width: 96,
                              height: 96,
                              bgcolor: selectedPhotoFilename === 'none' ? '#475569' : '#257eb9',
                              fontSize: '2.2rem',
                              fontWeight: 700,
                              boxShadow: '0 6px 18px rgba(37, 126, 185, 0.25)',
                              border: '3px solid #ffffff',
                              mb: 1.5,
                            }}
                          >
                            {userInitials}
                          </Avatar>
                          {selectedPhotoFilename === 'none' || photoSource?.type === 'none' ? (
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
                              <Chip
                                size="small"
                                color="default"
                                label="Sin foto asignada (Iniciales)"
                                sx={{ maxWidth: '100%', fontWeight: 700, fontSize: '0.72rem', bgcolor: '#e2e8f0', color: '#334155' }}
                              />
                              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.7rem' }}>
                                Opción manual: Ninguna foto
                              </Typography>
                            </Box>
                          ) : photoSource?.type === 'legacy_intranet' ? (
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
                              <Chip
                                size="small"
                                color="warning"
                                label={photoSource.isNewHire ? 'Origen: Intranet Antigua (Última Incorporación)' : `Origen: ${photoSource.label}`}
                                sx={{ maxWidth: '100%', fontWeight: 700, fontSize: '0.72rem' }}
                              />
                              <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600, fontSize: '0.7rem', wordBreak: 'break-all' }}>
                                Archivo: {photoSource.filename}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              size="small"
                              color={selectedPhotoFilename ? 'primary' : 'default'}
                              label={
                                selectedPhotoFilename
                                  ? `Manual: ${selectedPhotoFilename}`
                                  : photoSource?.type === 'azure_auto'
                                    ? `Azure: ${photoSource.filename}`
                                    : 'Modo automático (DNI / Nombre)'
                              }
                              sx={{ maxWidth: '100%', fontWeight: 600, fontSize: '0.72rem' }}
                            />
                          )}
                        </Box>
                      </Grid>

                      {/* Suggested Photos Grid */}
                      <Grid item xs={12} md={9} sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
                        {photoSource?.type === 'legacy_intranet' && (
                          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                            <strong>Origen de la imagen:</strong> Este usuario no dispone de foto en el catálogo de Azure Blob Storage (<code>fotos_rrhh</code>). Se está mostrando de forma provisional la imagen de su <strong>{photoSource.isNewHire ? 'anuncio de incorporación' : 'ficha'} en la intranet antigua</strong> (<code>{photoSource.filename}</code>). Cuando se suba su foto a Azure Blob Storage o se asigne una de las sugerencias, se actualizará automáticamente.
                          </Alert>
                        )}
                        {loadingPhotos ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1.5 }}>
                            <CircularProgress size={26} />
                            <Typography variant="body2" color="text.secondary">
                              Buscando sugerencias de fotos en Azure Blob Storage...
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                              gap: 1.5,
                              maxHeight: 460,
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              width: '100%',
                              boxSizing: 'border-box',
                              pr: 1,
                              '&::-webkit-scrollbar': { width: '8px' },
                              '&::-webkit-scrollbar-track': { bgcolor: '#f1f5f9', borderRadius: '4px' },
                              '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: '4px', '&:hover': { bgcolor: '#94a3b8' } },
                            }}
                          >
                            {/* Option 0: Ninguna (Sin foto) */}
                            {(() => {
                              const isNoneActive = selectedPhotoFilename === 'none' || photoSource?.type === 'none';
                              return (
                                <Box
                                  key="photo-option-none"
                                  onClick={() => handleSelectPhoto('none')}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.25,
                                    borderRadius: 2.5,
                                    cursor: savingPhoto ? 'wait' : 'pointer',
                                    bgcolor: isNoneActive ? '#f0f9ff' : '#ffffff',
                                    border: isNoneActive ? '2px solid #257eb9' : '1px solid #e2e8f0',
                                    boxShadow: isNoneActive ? '0 4px 14px rgba(37, 126, 185, 0.16)' : 'none',
                                    transition: 'all 0.2s ease',
                                    minWidth: 0,
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      borderColor: '#257eb9',
                                      bgcolor: '#f8fafc',
                                      transform: 'translateY(-2px)',
                                    },
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      bgcolor: isNoneActive ? '#257eb9' : '#94a3b8',
                                      color: '#ffffff',
                                      border: '2px solid #ffffff',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <NoPhotographyIcon fontSize="small" />
                                  </Avatar>
                                  <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                    <Typography
                                      variant="body2"
                                      title="Ninguna (Sin foto)"
                                      noWrap
                                      sx={{
                                        fontWeight: 700,
                                        color: '#1e293b',
                                        fontSize: '0.82rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      Ninguna (Sin foto)
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{
                                        display: 'block',
                                        color: '#64748b',
                                        fontSize: '0.72rem',
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      Mostrar iniciales ({userInitials})
                                    </Typography>
                                    {isNoneActive ? (
                                      <Chip
                                        icon={<CheckCircleIcon sx={{ fontSize: '0.95rem !important' }} />}
                                        label="Seleccionada"
                                        size="small"
                                        color="primary"
                                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                                      />
                                    ) : (
                                      <Chip
                                        label="Usar esta opción"
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              );
                            })()}

                            {/* Alerta cuando no hay sugerencias en Blob */}
                            {photoSuggestions.length === 0 && (
                              <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                                <Alert severity="info" sx={{ borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                                  No se han encontrado fotos sugeridas en Azure Blob Storage para <strong>{[firstSurname, secondSurname, name].filter(Boolean).join(' ')}</strong> ({dni || 'sin DNI'}). Puedes buscar en el catálogo o mantener la opción <strong>&quot;Ninguna&quot;</strong>.
                                </Alert>
                              </Box>
                            )}

                            {photoSuggestions.map((item) => {
                              const previewSrc = getAuthenticatedAvatarUrl(item.previewUrl);
                              const isActive = Boolean(item.isActive);
                              return (
                                <Box
                                  key={item.blobName}
                                  onClick={() => handleSelectPhoto(item.filename)}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.25,
                                    borderRadius: 2.5,
                                    cursor: savingPhoto ? 'wait' : 'pointer',
                                    bgcolor: isActive ? '#f0f9ff' : '#ffffff',
                                    border: isActive ? '2px solid #257eb9' : '1px solid #e2e8f0',
                                    boxShadow: isActive ? '0 4px 14px rgba(37, 126, 185, 0.16)' : 'none',
                                    transition: 'all 0.2s ease',
                                    minWidth: 0,
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      borderColor: '#257eb9',
                                      bgcolor: '#f8fafc',
                                      transform: 'translateY(-2px)',
                                    },
                                  }}
                                >
                                  <Avatar
                                    src={previewSrc || undefined}
                                    alt={item.filename}
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      bgcolor: '#257eb9',
                                      border: '2px solid #ffffff',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {name ? name.charAt(0).toUpperCase() : '📷'}
                                  </Avatar>
                                  <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                    <Typography
                                      variant="body2"
                                      title={item.filename}
                                      noWrap
                                      sx={{
                                        fontWeight: 700,
                                        color: '#1e293b',
                                        fontSize: '0.82rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {item.filename}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{
                                        display: 'block',
                                        color: '#64748b',
                                        fontSize: '0.72rem',
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {item.matchLabel}
                                    </Typography>
                                    {isActive ? (
                                      <Chip
                                        icon={<CheckCircleIcon sx={{ fontSize: '0.95rem !important' }} />}
                                        label="Seleccionada"
                                        size="small"
                                        color="primary"
                                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                                      />
                                    ) : (
                                      <Chip
                                        label="Usar esta foto"
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {/* Si el usuario tiene permisos de edición general (WRITE_USERS / ADMIN), mostramos el resto de secciones */}
            {canEditUserData && (
              <>
                {/* Datos Personales */}
                <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, border: 'none', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PersonIcon sx={{ color: '#1976d2', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" color="#333">{t('user.form.section.personal')}</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t('user.form.name')} value={name} onChange={(e) => setName(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t('user.form.firstSurname')} value={firstSurname} onChange={(e) => setFirstSurname(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t('user.form.secondSurname')} value={secondSurname} onChange={(e) => setSecondSurname(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t('user.form.dni')} value={dni} onChange={(e) => setDni(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t('user.form.gender')} value={gender} onChange={(e) => setGender(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="date" InputLabelProps={{ shrink: true }} label={t('user.form.birthDate')} value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

            {/* Datos de Contacto */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, border: 'none', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <ContactPhoneIcon sx={{ color: '#2e7d32', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" color="#333">{t('user.form.section.contact')}</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField fullWidth required label={t('user.form.email')} value={email} onChange={(e) => setEmail(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t('user.form.phone')} value={personalPhone} onChange={(e) => setPersonalPhone(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t('user.form.workPhone')} value={workPhone} onChange={(e) => setWorkPhone(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t('user.form.extension')} value={extension} onChange={(e) => setExtension(e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label={t('user.form.shortName')} value={shortName} onChange={(e) => setShortName(e.target.value)} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

            {/* Datos de Empresa */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: 'none', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <BusinessIcon sx={{ color: '#ed6c02', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" color="#333">{t('user.form.section.company')}</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={companies}
                      getOptionLabel={(option) => option.name || ''}
                      value={companies.find(c => c.id === companyId) || null}
                      onChange={(event, newValue) => setCompanyId(newValue ? newValue.id : '')}
                      renderInput={(params) => <TextField {...params} label={t('user.form.company')} variant="outlined" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={delegations}
                      getOptionLabel={(option) => option.name || ''}
                      value={delegations.find(d => d.id === delegationId) || null}
                      onChange={(event, newValue) => setDelegationId(newValue ? newValue.id : '')}
                      renderInput={(params) => <TextField {...params} label={t('user.form.delegation')} variant="outlined" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={jobPositions}
                      getOptionLabel={(option) => option.name || ''}
                      value={jobPositions.find(j => j.id === jobPositionId) || null}
                      onChange={(event, newValue) => setJobPositionId(newValue ? newValue.id : '')}
                      renderInput={(params) => <TextField {...params} label={t('user.form.jobPosition')} variant="outlined" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      options={professionalCategories}
                      getOptionLabel={(option) => option.name || ''}
                      value={professionalCategories.find(p => p.id === professionalCategoryId) || null}
                      onChange={(event, newValue) => setProfessionalCategoryId(newValue ? newValue.id : '')}
                      renderInput={(params) => <TextField {...params} label={t('user.form.professionalCategory')} variant="outlined" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t('user.form.workerId')} value={workerId} onChange={(e) => setWorkerId(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t('user.form.iban')} value={iban} onChange={(e) => setIban(e.target.value)} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

            {/* Configuración Laboral */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: 'none', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <WorkOutlineIcon sx={{ color: '#9c27b0', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" color="#333">{t('user.form.section.labor')}</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="date" InputLabelProps={{ shrink: true }} label={t('user.form.hireDate')} value={hireDate} onChange={(e) => setHireDate(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="date" InputLabelProps={{ shrink: true }} label={t('user.form.terminationDate')} value={terminationDate} onChange={(e) => setTerminationDate(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={isDirectEmployee} onChange={(e) => setIsDirectEmployee(e.target.checked)} />} label={t('user.form.isDirectEmployee')} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={commissionsCommercialExtras} onChange={(e) => setCommissionsCommercialExtras(e.target.checked)} />} label={t('user.form.commissionsExtras')} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Checkbox checked={commissionsAnnexes} onChange={(e) => setCommissionsAnnexes(e.target.checked)} />} label={t('user.form.commissionsAnnexes')} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

            {/* Seguridad y Accesos */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: 'none', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <SecurityIcon sx={{ color: '#d32f2f', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" color="#333">{t('user.form.section.security')}</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth {...(id === "new" ? { required: true } : {})} label={t('user.form.password')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth {...(id === "new" ? { required: true } : {})} label={t('user.form.password_two')} type="password" value={passwordTwo} onChange={(e) => setPasswordTwo(e.target.value)} />
                  </Grid>
                  {isAdmin() && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t('user.form.role')}</InputLabel>
                        <Select label={t('user.form.role')} value={role} onChange={(e) => setRole(e.target.value)}>
                          <MenuItem value="ROLE_USER">{t('user.form.role_user')}</MenuItem>
                          <MenuItem value="ROLE_ADMIN">{t('user.form.role_admin')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />} label={t('user.form.enabled')} />
                  </Grid>
                </Grid>

                {/* Información de permisos por Puesto de Trabajo */}
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <WorkOutlineIcon color="primary" fontSize="small" />
                    Permisos por Puesto de Trabajo
                  </Typography>
                  {currentJobPosition ? (
                    currentJobPosition.permissionGroup ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <strong>Puesto:</strong> {currentJobPosition.name} &nbsp;|&nbsp; 
                        <strong> Grupo de permisos asignado:</strong> {currentJobPosition.permissionGroup.description || currentJobPosition.permissionGroup.sku}
                        <br />
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Este usuario recibe automáticamente todos los permisos asociados a este grupo según su puesto.
                        </Typography>
                        {inheritedPermissions.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1.5 }}>
                            {inheritedPermissions.map((ip) => (
                              <Chip
                                key={ip.sku}
                                size="small"
                                label={`${ip.description || ip.sku} (${ip.category || 'GENERAL'})`}
                                color="info"
                                variant="filled"
                                icon={<LockIcon />}
                              />
                            ))}
                          </Box>
                        )}
                      </Alert>
                    ) : (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        El puesto asignado (<strong>{currentJobPosition.name}</strong>) no tiene ningún grupo de permisos configurado en la sección de administración de puestos.
                      </Alert>
                    )
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Selecciona un puesto de trabajo arriba en los datos laborales para asignar automáticamente su grupo de permisos.
                    </Alert>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Permisos Ad-hoc (Específicos del Usuario) */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon color="primary" fontSize="small" />
                      Permisos Ad-hoc (Específicos del Usuario)
                    </Typography>
                    <Chip
                      size="small"
                      label={`${selectedPermissions.length} permisos ad-hoc concedidos`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Puedes asignar permisos adicionales de forma individual a este usuario. Se organizan por categoría para facilitar su búsqueda.
                  </Typography>

                  {Object.keys(permissionsByCategory).length === 0 ? (
                    <Typography variant="body2">{t('user.form.no_permissions')}</Typography>
                  ) : (
                    Object.keys(permissionsByCategory).map((catName) => {
                      const catPerms = permissionsByCategory[catName];
                      const nonInheritedPerms = catPerms.filter((p) => !inheritedSkus.includes(p.sku));
                      const selectedCount = nonInheritedPerms.filter((p) => selectedPermissions.includes(p.sku)).length;
                      const allSelected = nonInheritedPerms.length > 0 && selectedCount === nonInheritedPerms.length;

                      return (
                        <Accordion key={catName} defaultExpanded sx={{ mb: 1.5, border: '1px solid #e0e0e0', boxShadow: 'none', '&:before': { display: 'none' }, borderRadius: '8px !important' }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <CategoryIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {catName}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`${selectedCount} / ${nonInheritedPerms.length} ad-hoc`}
                                  color={selectedCount > 0 ? 'primary' : 'default'}
                                  variant={selectedCount > 0 ? 'filled' : 'outlined'}
                                />
                              </Stack>
                              {nonInheritedPerms.length > 0 && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleCategory(catPerms);
                                  }}
                                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  {allSelected ? 'Desmarcar categoría' : 'Marcar categoría'}
                                </Button>
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 3, bgcolor: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
                            {catPerms.map((p) => {
                              const isInherited = inheritedSkus.includes(p.sku);
                              return (
                                <FormGroup key={p.sku}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isInherited || selectedPermissions.includes(p.sku)}
                                        onChange={handlePermissionChange}
                                        value={p.sku}
                                        disabled={isInherited}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                                        <span>{p.description}</span>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', bgcolor: '#eee', px: 0.8, py: 0.2, borderRadius: 1 }}>
                                          {p.sku}
                                        </Typography>
                                        {isInherited && (
                                          <Chip
                                            size="small"
                                            label="Heredado del puesto"
                                            color="info"
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                          />
                                        )}
                                      </Box>
                                    }
                                  />
                                </FormGroup>
                              );
                            })}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Grupos de Permisos Adicionales */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    {t('user.form.permission_groups')} (Adicionales)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Puedes asignar grupos de permisos adicionales directamente a este usuario además de los del puesto.
                  </Typography>
                  {permissionGroups.length === 0 ? (
                    <Typography variant="body2">{t('user.form.no_permission_groups')}</Typography>
                  ) : (
                    <Grid container spacing={1}>
                      {permissionGroups.map((pg) => (
                        <Grid item xs={12} sm={6} key={pg.id}>
                          <FormGroup>
                            <FormControlLabel
                              control={<Checkbox checked={selectedPermissionGroups.includes(pg.sku)} onChange={handlePermissionGroupChange} value={pg.sku} />}
                              label={
                                <Box>
                                  <Typography variant="body2" fontWeight="500">{pg.description}</Typography>
                                  <Typography variant="caption" color="text.secondary">{pg.sku}</Typography>
                                </Box>
                              }
                            />
                          </FormGroup>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>

              </CardContent>
            </Card>
          </Grid>
              </>
            )}

            {/* Botones */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 2 }}>
                <Box>
                  {isAdmin() && id !== "new" && String(id) !== String(getMeFromLocalStorage()?.id) && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<SwitchAccountIcon />}
                      onClick={() => setOpenImpersonateModal(true)}
                      sx={{ borderRadius: 2, px: 3, py: 1.5, textTransform: 'none', fontWeight: 'bold' }}
                    >
                      Entrar como este usuario
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={canEditUserData ? "outlined" : "contained"}
                    color={canEditUserData ? "inherit" : "primary"}
                    size="large"
                    onClick={() => navigate(isAdmin() ? PRIVATE_ADMIN_LIST_USERS : PRIVATE_USER_LIST_USERS)}
                    sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {canEditUserData ? t('user.form.cancel') : 'Volver al listado'}
                  </Button>
                  {canEditUserData && (
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                      sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 'bold', boxShadow: '0 4px 12px 0 rgba(25, 118, 210, 0.3)' }}
                    >
                      {id === "new" ? t('user.form.create') : t('user.form.update')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Modal para confirmar entrar como usuario (impersonate) */}
      <Dialog 
        open={openImpersonateModal} 
        onClose={() => setOpenImpersonateModal(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          {t('user.table.modal.impersonate.title') || 'Entrar como usuario'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1">
            {t('user.table.modal.impersonate.phrase') || '¿Estás seguro de que quieres iniciar sesión como este usuario?'}
          </Typography>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {[name, firstSurname, secondSurname].filter(Boolean).join(' ') || 'Usuario'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {email}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Podrás volver a tu usuario de administrador en cualquier momento con el botón &quot;Volver usuario original&quot;.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenImpersonateModal(false)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('user.table.modal.impersonate.button.cancel') || 'Cancelar'}
          </Button>
          <Button 
            variant="contained" 
            color="warning" 
            onClick={handleConfirmImpersonate}
            startIcon={<SwitchAccountIcon />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', px: 2 }}
          >
            {t('user.table.modal.impersonate.button.confirm') || 'Entrar como'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogLoader open={sendingApiRequest} />
    </Box>
  );
}

export default FormUserView;
